import { inject, injectable } from 'inversify';
import { DI_TYPES } from '../../di-types';
import { readonly } from '../../util/readonly-proxy';
import {
  getTimezone as _getTimezone,
  timestampFactory as _timestampFactory,
} from '../../util/time';
import { getWindowObject as _getWindowObject } from '../../util/window-provider';
import { getBrowserInfo } from './browser';
import { CustomProperties, ClientStateRoot } from './schema';
import { extendClientStateRoot as addShopifyField } from './shopifyV2';
import { extendClientStateRoot as addShopRenterField } from './shoprenterV2';
import { V2ClientFeatureState } from './features';

@injectable()
export class V2ClientStateFactory {
  private timestampFactory: typeof _timestampFactory;
  private getTimezone: typeof _getTimezone;
  private customProperties: CustomProperties = {};
  private getWindowObject: typeof _getWindowObject;
  private featureState: V2ClientFeatureState;

  public constructor(
    @inject(DI_TYPES.TimestampFactory) timestampFactory: typeof _timestampFactory,
    @inject(DI_TYPES.getTimezone) getTimezone: typeof _getTimezone,
    @inject(DI_TYPES.WindowProvider) getWindowObject: typeof _getWindowObject,
    @inject(DI_TYPES.V2ClientFeatureState) featureState: V2ClientFeatureState
  ) {
    this.timestampFactory = timestampFactory;
    this.getTimezone = getTimezone;
    this.getWindowObject = getWindowObject;
    this.featureState = featureState;
  }

  public addCustomProperty(name: string, value: Object) {
    this.customProperties[name] = value;
  }

  public getRoot(): Readonly<ClientStateRoot> {
    const windowObject = this.getWindowObject();
    const rootFactory = this;
    const root: ClientStateRoot = {
      custom: this.customProperties,
      time: {
        get timezone(): string {
          return rootFactory.getTimezone();
        },
        get time(): Date {
          return rootFactory.timestampFactory();
        },
        get timestamp(): number {
          return +rootFactory.timestampFactory();
        },
      },
      browser: getBrowserInfo(windowObject),
      features: this.featureState.getFeatureStates(),
    };
    addShopifyField(root, windowObject);
    addShopRenterField(root, windowObject);
    return readonly(root);
  }
}
